import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  HeartHandshake,
  LayoutDashboard,
  LogOut,
  KanbanSquare,
  MessageSquare,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Avatar, Badge, Button, cn, useBranding } from '@4zen/ui';
import { useAuth } from '@/stores/auth';
import { useRealtime } from '@/lib/realtime';
import { useDesktopIntegration } from '@/lib/useDesktop';
import { useOnlineStatus } from '@/lib/useOnlineStatus';
import { useOrganization } from '@/features/admin/queries';
import { IncomingCallToast } from '@/features/comms/IncomingCallToast';
import { LicenseBanner } from './LicenseBanner';
import { useUnreadCounters } from '@/features/comms/queries';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission?: string;
  feature?: string;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Мій день', icon: LayoutDashboard },
  { to: '/calendar', label: 'Календар', icon: CalendarDays, permission: 'task:read' },
  { to: '/clients', label: 'Клієнти', icon: Building2, permission: 'client:read' },
  { to: '/inbox', label: 'Комунікації', icon: MessageSquare, permission: 'comms:read' },
  {
    to: '/sales',
    label: 'Продажі',
    icon: ScrollText,
    permission: 'sales:read',
    feature: 'sales_scripts',
  },
  {
    to: '/scrum',
    label: 'Проєкти',
    icon: KanbanSquare,
    permission: 'scrum:read',
    feature: 'ai_scrum',
  },
  {
    to: '/analytics',
    label: 'Аналітика',
    icon: BarChart3,
    // Everyone may read their own figures; the page widens itself for a head.
    permission: 'analytics:self',
  },
  {
    to: '/retention',
    label: 'Утримання',
    icon: HeartHandshake,
    permission: 'retention:read',
    feature: 'retention',
  },
  {
    to: '/assistant',
    label: 'AI-асистент',
    icon: Sparkles,
    permission: 'ai:use',
    feature: 'ai_core',
  },
  { to: '/audit', label: 'Журнал змін', icon: ShieldCheck, permission: 'audit:read' },
];

export function AppShell() {
  const { user, logout, can, hasFeature } = useAuth();
  const navigate = useNavigate();
  const { data: organization } = useOrganization();
  const { data: unread } = useUnreadCounters();
  const [incomingCallEnabled] = useState(true);

  const online = useOnlineStatus();

  useBranding(organization?.branding);
  useRealtime();
  useDesktopIntegration();

  const visibleNav = NAV.filter(
    (item) =>
      (!item.permission || can(item.permission)) && (!item.feature || hasFeature(item.feature)),
  );

  const branding = organization?.branding ?? {};
  // A white-label tenant that has not uploaded a logo yet should see its own
  // initials rather than ours; falling back to "4Z" would undo the point.
  const productName =
    branding.product_name ?? (branding.hide_vendor_branding ? (organization?.name ?? '') : '4ZEN CRM');
  const monogram = productName.trim().slice(0, 2).toUpperCase() || '4Z';

  return (
    <div className="flex h-full">
      <aside className="hidden w-sidebar shrink-0 flex-col border-r border-line bg-surface lg:flex">
        <div className="flex h-topbar items-center gap-2 px-5">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="" className="h-8 w-8 rounded object-contain" />
          ) : (
            <span className="grid h-8 w-8 place-items-center rounded bg-accent text-sm font-bold text-accent-contrast">
              {monogram}
            </span>
          )}
          <span className="truncate text-sm font-semibold">{productName}</span>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors duration-fast',
                  isActive
                    ? 'bg-accent-soft font-medium text-accent'
                    : 'text-content-muted hover:bg-surface-muted hover:text-content',
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
              {item.to === '/inbox' && unread && unread.total > 0 ? (
                <Badge tone="accent" className="ml-auto">
                  {unread.total}
                </Badge>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-line p-3">
          <div className="flex items-center gap-3 rounded px-2 py-2">
            <Avatar name={user?.full_name ?? '?'} src={user?.avatar_url} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.full_name}</p>
              <p className="truncate text-xs text-content-subtle">{user?.position ?? user?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Вийти"
              onClick={() => {
                void logout().then(() => navigate('/login'));
              }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-topbar shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="flex h-9 flex-1 items-center gap-2 rounded border border-line bg-surface-muted px-3 text-sm text-content-subtle transition-colors hover:border-line-strong lg:max-w-md"
          >
            <Search className="h-4 w-4" />
            Пошук клієнтів, задач, розмов…
            <kbd className="ml-auto rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[10px]">
              ⌘K
            </kbd>
          </button>

          <div className="ml-auto flex items-center gap-1">
            {online ? null : (
              <Badge tone="warning" title="Дані читаються з локального кешу">
                Офлайн
              </Badge>
            )}
            {user ? <Badge tone="accent">{user.tariff}</Badge> : null}
            <Button variant="ghost" size="icon" aria-label="Сповіщення" onClick={() => navigate('/notifications')}>
              <Bell className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Налаштування" onClick={() => navigate('/settings')}>
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <LicenseBanner />

        <main className="zen-scroll-area flex-1 bg-bg">
          <Outlet />
        </main>
      </div>

      {incomingCallEnabled ? <IncomingCallToast /> : null}
    </div>
  );
}
